import dev.shanty.kotwire.stimulus.Controller
import dev.shanty.kotwire.stimulus.StimulusController
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

@JsExport
@StimulusController(
    targets = ["list", "item", "showMore", "typeButton", "openNowButton", "searchInput"],
    values = PlaceSortController.Values::class
)
class PlaceSortController(context: dynamic) : Controller(context) {

    object Values {
        val limit: Int = 0
        val expanded: Boolean = false
    }

    private val list           by listTarget<HTMLElement>()
    private val items          by itemTargets<HTMLElement>()
    private val limit          by limitValue()
    private val expanded       by expandedValue()
    private val showMores      by showMoreTargets<HTMLElement>()
    private val typeButtons    by typeButtonTargets<HTMLElement>()
    private val openNowButtons by openNowButtonTargets<HTMLElement>()

    private var cachedLat: Double? = null
    private var cachedLng: Double? = null
    private var activeType: String = ""
    private var showOpenOnly: Boolean = false
    private var searchQuery: String = ""

    override fun connect() {
        activeType = typeButtons.firstOrNull { it.classList.contains("bg-primary") }
                         ?.getAttribute("data-place-type") ?: ""
        showOpenOnly = openNowButtons.any { it.classList.contains("bg-green-600") }
        sortByDistance()
    }

    @JsName("filterType")
    fun filterType(e: Event) {
        val button = e.currentTarget.asDynamic().unsafeCast<HTMLElement>()
        activeType = button.getAttribute("data-place-type") ?: ""
        typeButtons.forEach { btn ->
            if (btn.getAttribute("data-place-type") == activeType) {
                btn.classList.remove("bg-gray-100", "text-gray-600")
                btn.classList.add("bg-primary", "text-white")
            } else {
                btn.classList.remove("bg-primary", "text-white")
                btn.classList.add("bg-gray-100", "text-gray-600")
            }
        }
        applyFiltersAndSort()
    }

    @JsName("toggleOpenNow")
    fun toggleOpenNow(e: Event) {
        showOpenOnly = !showOpenOnly
        openNowButtons.forEach { btn ->
            if (showOpenOnly) {
                btn.classList.remove("bg-gray-100", "text-gray-600")
                btn.classList.add("bg-green-600", "text-white")
            } else {
                btn.classList.remove("bg-green-600", "text-white")
                btn.classList.add("bg-gray-100", "text-gray-600")
            }
        }
        applyFiltersAndSort()
    }

    @JsName("filterBySearch")
    fun filterBySearch(e: Event) {
        searchQuery = e.currentTarget.asDynamic().unsafeCast<HTMLInputElement>().value.trim().lowercase()
        applyFiltersAndSort()
    }

    @JsName("sortByDistance")
    fun sortByDistance() {
        val geo = window.navigator.asDynamic().geolocation ?: return
        geo.getCurrentPosition({ position: dynamic ->
            cachedLat = position.coords.latitude as Double
            cachedLng = position.coords.longitude as Double
            applyFiltersAndSort()
        })
    }

    @JsName("showAll")
    fun showAll() {
        element.setAttribute("data-placesort-expanded-value", "true")
        items.forEach {
            if (it.getAttribute("data-filtered-out") != "true") it.classList.remove("hidden")
        }
        showMores.forEach { it.classList.add("hidden") }
    }

    private fun applyFiltersAndSort() {
        items.forEach { item ->
            val type = item.getAttribute("data-place-type") ?: ""
            val open = item.getAttribute("data-place-open") ?: "unknown"
            val searchable = item.getAttribute("data-place-search") ?: ""

            val typeMatch   = activeType.isEmpty() || type == activeType
            val openMatch   = !showOpenOnly || open == "true"
            val searchMatch = searchQuery.isEmpty() || searchable.contains(searchQuery)

            item.setAttribute("data-filtered-out", if (typeMatch && openMatch && searchMatch) "false" else "true")
        }

        val userLat = cachedLat
        val userLng = cachedLng
        val (filteredOut, visible) = items.partition { it.getAttribute("data-filtered-out") == "true" }

        val sorted = if (userLat != null && userLng != null) {
            visible
                .map { item ->
                    val lat = item.getAttribute("data-latitude")?.toDoubleOrNull() ?: 0.0
                    val lng = item.getAttribute("data-longitude")?.toDoubleOrNull() ?: 0.0
                    Pair(item, haversineKm(userLat, userLng, lat, lng))
                }
                .sortedBy { it.second }
        } else {
            visible.map { Pair(it, 0.0) }
        }

        sorted.forEachIndexed { index, (item, dist) ->
            if (userLat != null) item.querySelector(".place-distance")?.textContent = formatDistance(dist)
            val lim = if (expanded) 0 else limit.toInt()
            if (lim > 0 && index >= lim) item.classList.add("hidden")
            else item.classList.remove("hidden")
            list.appendChild(item)
        }

        filteredOut.forEach { item ->
            item.classList.add("hidden")
            list.appendChild(item)
        }

        val hasHiddenByLimit = !expanded && limit.toInt() > 0 && visible.size > limit.toInt()
        showMores.forEach { btn ->
            if (hasHiddenByLimit) btn.classList.remove("hidden") else btn.classList.add("hidden")
        }
    }

    private fun haversineKm(lat1: Double, lng1: Double, lat2: Double, lng2: Double): Double {
        val R = 6371.0
        val dLat = (lat2 - lat1) * PI / 180
        val dLng = (lng2 - lng1) * PI / 180
        val sinLat = sin(dLat / 2)
        val sinLng = sin(dLng / 2)
        val a = sinLat * sinLat + cos(lat1 * PI / 180) * cos(lat2 * PI / 180) * sinLng * sinLng
        return R * 2 * atan2(sqrt(a), sqrt(1 - a))
    }

    private fun formatDistance(km: Double): String {
        return if (km < 1.0) "${(km * 1000).toInt()} m away"
        else "${(kotlin.math.round(km * 10) / 10)} km away"
    }
}
